import { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { SubscriptionOptions } from '@reduxjs/toolkit/dist/query/core/apiState';
import {
  LazyQueryTrigger,
  UseLazyQuery,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { IGetQueryParams, IPaginatedRepsonse } from 'app/services';
import { errorToast } from 'Components/Notifications';
import CryptoJS from 'crypto-js';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import useUndo from 'use-undo';
import { useWindowQueryParams } from './useWindowQueryParams';
export interface PageData<R> {
  data: R[];
  page: number;
}

const SECRET = 'aoa-aoa';

function getData<P, R>(
  query: LazyQueryTrigger<
    QueryDefinition<IGetQueryParams<P>, any, any, IPaginatedRepsonse<R>, string>
  >,
) {
  return (
    params: IGetQueryParams<P>,
    cache: PageData<R>[],
    pagination: { count: number; pages: number },
  ): Promise<{
    allPages: PageData<R>[];
    current: PageData<R>;
    pagination: { count: number; pages: number };
    queryParams: {
      page: number;
      per_page: number;
      filters: P;
    };
  }> => {
    let current = cache.find((cp) => cp.page === params.page);

    if (current) {
      return Promise.resolve({
        allPages: cache,
        current,
        pagination,
        queryParams: {
          filters: params.filters!,
          page: current.page,
          per_page: params.per_page ? params.per_page : 2,
        },
      });
    } else {
      return query(params)
        .unwrap()
        .then(({ pagination, data }) => {
          const current = {
            page: pagination.page,
            data,
          };
          return {
            allPages: cache
              .concat(current)
              .slice()
              .sort((a, b) => a.page - b.page),
            current,
            pagination,
            queryParams: {
              filters: params.filters!,
              page: current.page,
              per_page: params.per_page ? params.per_page : 2,
            },
          };
        });
    }
  };
}

export default function useCachePagination<P, R>(
  lazyQuery: UseLazyQuery<
    QueryDefinition<IGetQueryParams<P>, any, any, IPaginatedRepsonse<R>>
  >,
  initialState: {
    allPages: PageData<R>[];
    current?: PageData<R> | undefined;
    pagination: {
      count: number;
      pages: number;
    };
    queryParams: { page: number; filters: P; per_page: number };
  },
  subscriptionOptions?: SubscriptionOptions,
  skipQueryParamsCache?: boolean,
) {
  const [fetch, { data, isLoading }] = lazyQuery(subscriptionOptions);

  const [searchParams, setSearchParams] = useWindowQueryParams(
    initialState.queryParams,
    {
      skip: skipQueryParamsCache,
    },
  );

  const [{ present: queryParams }, ParamsHistory] = useUndo(searchParams, {
    useCheckpoints: true,
  });

  const [state, setState] = useState<{
    allPages: PageData<R>[];
    current?: PageData<R>;
    queryParams: { page: number; per_page: number; filters: P };
    pagination: { count: number; pages: number };
  }>({
    ...initialState,
    // @ts-ignore
    queryParams,
  });

  const [isFetching, setIsFetching] = useState<boolean>(false);

  const GetData = (
    params: IGetQueryParams<P>,
    cache: PageData<R>[],
    checkPoint = false,
  ) => {
    setIsFetching((_) => true);
    let newP = {
      ...params,
      per_page: params.per_page || 20,
      page: params.page || 1,
    };
    // @ts-ignore
    setSearchParams(newP);
    ParamsHistory.set(newP, checkPoint);

    return getData(fetch)(params, cache, state.pagination)
      .then((s) => {
        setState((_) => s);
        setIsFetching((_) => false);

        return s;
      })
      .catch(() => {
        // if the error loading is because of bad cached params retry with initial state queryparams
        getData(fetch)(initialState.queryParams, [], state.pagination).then(
          (s) => {
            setState((_) => s);
            setIsFetching((_) => false);
            // @ts-ignore
            setSearchParams(initialState.queryParams);
            ParamsHistory.reset(initialState.queryParams);
            return s;
          },
        );
        errorToast('Error Loading Data');
        return;
      });
  };

  useEffect(() => {
    GetData(queryParams, []);
  }, [JSON.stringify(queryParams)]);

  const setFilters = (filters: P, checkPoint = false) => {
    const query = {
      filters,
      page: 1,
      per_page: initialState.queryParams.per_page,
    };

    return GetData(query, [], checkPoint);
  };

  return {
    ...state,
    isFetching,
    isLoading,
    data,
    GetData,
    setFilters,
    ...ParamsHistory,
  };
}
